import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Trash2, Send, CalendarPlus, ExternalLink, AlertTriangle, Wand2 } from "lucide-react";
import ScheduleDialog from "@/components/ScheduleDialog";
import { DeclinerPost } from "@/components/DeclinerPost";

const statusMeta = {
  draft: { label: "Brouillon", cls: "bg-slate-100 text-slate-600" },
  scheduled: { label: "Programmé", cls: "bg-coral-soft text-coral-dark" },
  published: { label: "Publié", cls: "bg-coral-soft text-coral-dark" },
  failed: { label: "Échec", cls: "bg-destructive/10 text-destructive" },
};

const filters = [
  { key: "all", label: "Tous" },
  { key: "draft", label: "Brouillons" },
  { key: "scheduled", label: "Programmés" },
  { key: "published", label: "Publiés" },
  { key: "failed", label: "Échecs" },
];

export default function Posts() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [schedulePost, setSchedulePost] = useState(null);

  const load = () => api.get("/posts").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.delete(`/posts/${id}`);
    toast.success("Post supprimé.");
    load();
  };

  const publish = async (id) => {
    try {
      await api.post(`/posts/${id}/publish`);
      toast.success("Post publié (simulation LinkedIn).");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const shown = filter === "all" ? posts : posts.filter((p) => p.status === filter);

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <div className="mb-2 flex items-center gap-2 text-coral-dark">
        <FileText className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em]">Journal</span>
      </div>
      <h1 className="font-serif text-4xl text-slate-900 mb-8">Mes posts</h1>

      <div className="flex flex-wrap gap-2 mb-6">
        {filters.map((f) => (
          <button key={f.key} data-testid={`filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === f.key ? "bg-coral-strong text-white" : "border border-slate-200 text-slate-600 hover:border-coral"
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-slate-200 rounded-sm">
          <p className="text-slate-400">Aucun post ici pour l'instant.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {shown.map((p) => (
            <div key={p.id} data-testid={`post-card-${p.id}`} className="bg-white border border-slate-200 rounded-sm">
              <div className="flex items-center justify-between px-6 py-3 border-b border-slate-100">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusMeta[p.status]?.cls}`}>
                  {statusMeta[p.status]?.label}
                </span>
                <span className="text-xs text-slate-400">
                  {p.status === "scheduled" && p.scheduled_at && `Programmé le ${new Date(p.scheduled_at).toLocaleString("fr-FR")}`}
                  {p.status === "published" && p.published_at && `Publié le ${new Date(p.published_at).toLocaleString("fr-FR")}`}
                </span>
              </div>
              <p className="px-6 py-4 font-serif text-[15px] leading-[1.7] text-slate-800 whitespace-pre-wrap">
                {p.content}
              </p>
              {p.image_data && (
                <div className="px-6 pb-4">
                  <img src={p.image_data} alt="visuel du post" className="max-h-56 rounded-sm border border-slate-200 object-cover" />
                </div>
              )}
              {p.status === "failed" && p.error && (
                <div className="flex items-center gap-2 px-6 pb-3 text-destructive text-xs">
                  <AlertTriangle className="h-4 w-4" /> {p.error} (tentatives : {p.attempts})
                </div>
              )}
              <div className="flex items-center gap-2 px-6 py-3 border-t border-slate-100">
                {p.status !== "published" && (
                  <>
                    <button data-testid={`publish-${p.id}`} onClick={() => publish(p.id)}
                      className="flex items-center gap-1.5 text-sm font-semibold text-white bg-coral-strong px-3.5 py-2 rounded-sm hover:bg-coral-hover transition-colors">
                      <Send className="h-4 w-4" /> Publier
                    </button>
                    <button data-testid={`schedule-post-${p.id}`} onClick={() => setSchedulePost(p)}
                      className="flex items-center gap-1.5 text-sm font-semibold text-coral-dark border border-coral px-3.5 py-2 rounded-sm hover:bg-coral-strong hover:text-white transition-colors">
                      <CalendarPlus className="h-4 w-4" /> {p.status === "scheduled" ? "Replanifier" : "Planifier"}
                    </button>
                  </>
                )}
                {p.post_url && (
                  <a href={p.post_url} target="_blank" rel="noreferrer" data-testid={`link-${p.id}`}
                    className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-coral-dark transition-colors">
                    <ExternalLink className="h-4 w-4" /> Voir
                  </a>
                )}
                <button data-testid={`delete-${p.id}`} onClick={() => remove(p.id)}
                  className="ml-auto flex items-center gap-1.5 text-sm text-slate-400 hover:text-destructive transition-colors">
                  <Trash2 className="h-4 w-4" /> Supprimer
                </button>
              </div>
              {p.image_prompt && (
                <div className="px-6 pb-3 -mt-1">
                  <button data-testid={`reuse-prompt-${p.id}`}
                    onClick={() => { sessionStorage.setItem("reuse_prompt", p.image_prompt); navigate("/creer"); }}
                    className="flex items-center gap-1.5 text-xs font-semibold text-coral-dark hover:text-coral-hover transition-colors">
                    <Wand2 className="h-3.5 w-3.5" /> Réutiliser ce prompt
                  </button>
                </div>
              )}
              <div className="px-6 pb-4">
                <DeclinerPost postId={p.id} imageUrl={p.image_data} context="posts" />
              </div>
            </div>
          ))}
        </div>
      )}

      {schedulePost && (
        <ScheduleDialog
          content={schedulePost.content}
          postId={schedulePost.id}
          initialImage={schedulePost.image_data}
          onClose={() => setSchedulePost(null)}
          onScheduled={() => { setSchedulePost(null); load(); }}
        />
      )}
    </div>
  );
}
